import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Bell, Plus, Trash2, Edit, Play, ExternalLink, CheckCircle2, XCircle, Clock, Loader2, Webhook } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Available webhook events
const WEBHOOK_EVENTS = [
  { id: 'new_violation', label: 'New Violation Detected', description: 'Triggered when a new violation is found' },
  { id: 'high_severity', label: 'High Severity Alert', description: 'Only for high severity violations' },
  { id: 'status_change', label: 'Status Change', description: 'When violation status changes' },
  { id: 'watchlist_update', label: 'Watchlist Update', description: 'When watchlist properties change' }
];

export default function WebhooksPage() {
  const [webhooks, setWebhooks] = useState([]);
  const [watchlists, setWatchlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [testing, setTesting] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    events: ['new_violation'],
    watchlist_ids: null,
    include_details: true,
    secret: '',
    headers: {}
  });

  const fetchWebhooks = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/webhooks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWebhooks(data.webhooks || []);
      }
    } catch (error) {
      console.error('Error fetching webhooks:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWatchlists = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/watchlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWatchlists(data.watchlists || []);
      }
    } catch (error) {
      console.error('Error fetching watchlists:', error);
    }
  }, []);

  useEffect(() => {
    fetchWebhooks();
    fetchWatchlists();
  }, [fetchWebhooks, fetchWatchlists]);

  const handleCreate = async () => {
    if (!formData.name || !formData.url) {
      toast.error('Name and URL are required');
      return;
    }

    if (!formData.url.startsWith('https://')) {
      toast.error('Webhook URL must use HTTPS');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const method = editingWebhook ? 'PUT' : 'POST';
      const endpoint = editingWebhook 
        ? `${API_URL}/api/webhooks/${editingWebhook.id}`
        : `${API_URL}/api/webhooks`;

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingWebhook ? 'Webhook updated' : 'Webhook created');
        setDialogOpen(false);
        resetForm();
        fetchWebhooks();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to save webhook');
      }
    } catch (error) {
      toast.error('Error saving webhook');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this webhook?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/webhooks/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Webhook deleted');
        fetchWebhooks();
      }
    } catch (error) {
      toast.error('Error deleting webhook');
    }
  };

  const handleToggle = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/webhooks/${id}/toggle`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchWebhooks();
      }
    } catch (error) {
      toast.error('Error toggling webhook');
    }
  };

  const handleTest = async (id) => {
    setTesting(id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/webhooks/${id}/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Test webhook delivered successfully!');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Test failed');
      }
    } catch (error) {
      toast.error('Error testing webhook');
    } finally {
      setTesting(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      events: ['new_violation'],
      watchlist_ids: null,
      include_details: true,
      secret: '',
      headers: {}
    });
    setEditingWebhook(null);
  };

  const openEditDialog = (webhook) => {
    setEditingWebhook(webhook);
    setFormData({
      name: webhook.name,
      url: webhook.url,
      events: webhook.events || ['new_violation'],
      watchlist_ids: webhook.watchlist_ids,
      include_details: webhook.include_details ?? true,
      secret: '',
      headers: webhook.headers || {}
    });
    setDialogOpen(true);
  };

  const toggleEvent = (eventId) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.includes(eventId)
        ? prev.events.filter(e => e !== eventId)
        : [...prev.events, eventId]
    }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Webhook className="w-6 h-6 text-indigo-400" />
              Webhook Notifications
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              Configure HTTP endpoints to receive violation alerts
            </p>
          </div>
          <Button
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="btn-primary"
            data-testid="create-webhook-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Webhook
          </Button>
        </div>

        {/* Info Card */}
        <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-4">
          <h3 className="text-indigo-300 font-medium mb-2">How Webhooks Work</h3>
          <p className="text-zinc-400 text-sm">
            When new violations are detected for your monitored properties, Insite360 will send an HTTP POST 
            request to your configured URL with violation details. This is perfect for integrating with 
            Slack, Microsoft Teams, Zapier, or your custom systems.
          </p>
        </div>

        {/* Webhooks List */}
        {webhooks.length === 0 ? (
          <div className="bg-zinc-800/50 rounded-lg p-8 text-center">
            <Bell className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Webhooks Configured</h3>
            <p className="text-zinc-400 mb-4">
              Create your first webhook to receive violation alerts via HTTP POST
            </p>
            <Button onClick={() => setDialogOpen(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Create Webhook
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {webhooks.map((webhook) => (
              <div
                key={webhook.id}
                className={`bg-zinc-800/50 border rounded-lg p-4 ${
                  webhook.enabled ? 'border-zinc-700' : 'border-zinc-800 opacity-60'
                }`}
                data-testid={`webhook-${webhook.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-medium text-white">{webhook.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        webhook.enabled 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {webhook.enabled ? 'Active' : 'Disabled'}
                      </span>
                      {webhook.last_status && (
                        <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${
                          webhook.last_status >= 200 && webhook.last_status < 300
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {webhook.last_status >= 200 && webhook.last_status < 300 
                            ? <CheckCircle2 className="w-3 h-3" />
                            : <XCircle className="w-3 h-3" />
                          }
                          Last: {webhook.last_status}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-zinc-400 mb-2">
                      <ExternalLink className="w-4 h-4" />
                      <code className="bg-zinc-900 px-2 py-0.5 rounded text-xs">
                        {webhook.url}
                      </code>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mb-2">
                      {webhook.events?.map(event => (
                        <span key={event} className="text-xs bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded">
                          {event.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                    
                    {webhook.last_triggered && (
                      <div className="flex items-center gap-1 text-xs text-zinc-500">
                        <Clock className="w-3 h-3" />
                        Last triggered: {new Date(webhook.last_triggered).toLocaleString()}
                      </div>
                    )}
                    
                    {webhook.failure_count > 0 && (
                      <div className="text-xs text-amber-400 mt-1">
                        {webhook.failure_count} failed delivery attempt(s)
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTest(webhook.id)}
                      disabled={testing === webhook.id}
                      className="text-zinc-400 hover:text-white"
                    >
                      {testing === webhook.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(webhook)}
                      className="text-zinc-400 hover:text-white"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Switch
                      checked={webhook.enabled}
                      onCheckedChange={() => handleToggle(webhook.id)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(webhook.id)}
                      className="text-zinc-400 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingWebhook ? 'Edit Webhook' : 'Create Webhook'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-zinc-300">Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Slack Notifications"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  data-testid="webhook-name-input"
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Webhook URL (HTTPS required)</Label>
                <Input
                  value={formData.url}
                  onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://hooks.slack.com/services/..."
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  data-testid="webhook-url-input"
                />
              </div>
              
              <div>
                <Label className="text-zinc-300 mb-2 block">Events to Trigger</Label>
                <div className="space-y-2">
                  {WEBHOOK_EVENTS.map(event => (
                    <div key={event.id} className="flex items-start gap-3">
                      <Checkbox
                        id={event.id}
                        checked={formData.events.includes(event.id)}
                        onCheckedChange={() => toggleEvent(event.id)}
                      />
                      <div>
                        <label htmlFor={event.id} className="text-sm text-white cursor-pointer">
                          {event.label}
                        </label>
                        <p className="text-xs text-zinc-500">{event.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-zinc-300">Include Violation Details</Label>
                  <p className="text-xs text-zinc-500">Send full violation info in payload</p>
                </div>
                <Switch
                  checked={formData.include_details}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_details: checked }))}
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Secret (Optional)</Label>
                <Input
                  type="password"
                  value={formData.secret}
                  onChange={(e) => setFormData(prev => ({ ...prev, secret: e.target.value }))}
                  placeholder="For HMAC signature verification"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  If set, requests will include X-Webhook-Signature header
                </p>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="ghost"
                  onClick={() => { setDialogOpen(false); resetForm(); }}
                  className="btn-ghost"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  className="btn-primary"
                  data-testid="save-webhook-btn"
                >
                  {editingWebhook ? 'Update Webhook' : 'Create Webhook'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
